import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DollarSign, TrendingUp, Calendar, ChevronLeft, ChevronRight, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { usePartnerAuth } from '@/hooks/usePartnerAuth';
import { supabase } from '@/integrations/supabase/client';
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

const ITEMS_PER_PAGE = 10;

interface PayoutRecord {
  id: string;
  deliveredAt: Date;
  memorialName: string;
  serviceType: string;
  amount: number;
  status: 'pending' | 'paid' | 'skipped';
}

const PartnerEarnings = () => {
  const navigate = useNavigate();
  const { partner, hasPermission, isLoading: partnerAuthLoading } = usePartnerAuth();
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [monthlyEarnings, setMonthlyEarnings] = useState(0);
  const [weeklyEarnings, setWeeklyEarnings] = useState(0);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [payouts, setPayouts] = useState<PayoutRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination calculations
  const totalPages = Math.ceil(payouts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedPayouts = payouts.slice(startIndex, endIndex);

  const canViewEarnings = hasPermission('view_earnings');

  useEffect(() => {
    if (!canViewEarnings) return;

    const fetchPayoutRecords = async () => {
      if (!partner?.id) return;

      try {
        const now = new Date();
        const weekStart = startOfWeek(now, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
        const monthStart = startOfMonth(now);
        const monthEnd = endOfMonth(now);

        const partnerId = partner.id;

        // Only fetch fulfillment payouts for this partner.
        // Commission records belong in the Monument Portal earnings, not here.
        const query = supabase
          .from('payout_records')
          .select(`
            id,
            delivered_at,
            payout_amount_cents,
            payout_status,
            partner_id,
            order_id,
            orders (
              service_type,
              memorial_id,
              memorials (
                first_name,
                last_name
              )
            )
          `)
          .eq('partner_id', partnerId)
          .neq('payout_status', 'skipped')
          .order('delivered_at', { ascending: false });

        const { data: records, error } = await query;

        if (error) {
          console.error('Error fetching payout records:', error);
          return;
        }

        if (records) {
          let total = 0;
          let monthly = 0;
          let weekly = 0;
          let pending = 0;
          const payoutsList: PayoutRecord[] = [];

          records.forEach((record) => {
            const amount = (record.payout_amount_cents || 0) / 100;
            const deliveredDate = record.delivered_at ? new Date(record.delivered_at) : new Date();
            const status = record.payout_status as 'pending' | 'paid' | 'skipped';

            // Get memorial info from nested join
            const order = record.orders as {
              service_type: string;
              memorial_id: string;
              memorials: { first_name: string; last_name: string | null } | null
            } | null;
            const serviceType = order?.service_type?.toLowerCase() || '';

            // Filter: only show payouts for service types this partner offers
            if (serviceType === 'flowers' && !partner.offers_flowers) return;
            if (serviceType === 'cleaning' && !partner.offers_cleaning) return;

            const memorial = order?.memorials;

            // Only count paid amounts in totals
            if (status === 'paid') {
              total += amount;

              if (deliveredDate >= monthStart && deliveredDate <= monthEnd) {
                monthly += amount;
              }
              if (deliveredDate >= weekStart && deliveredDate <= weekEnd) {
                weekly += amount;
              }
            } else if (status === 'pending') {
              pending += amount;
            }

            payoutsList.push({
              id: record.id,
              deliveredAt: deliveredDate,
              memorialName: memorial
                ? buildMemorialDisplayName(memorial)
                : 'Unknown',
              serviceType: order?.service_type || 'Unknown',
              amount,
              status,
            });
          });

          setTotalEarnings(total);
          setMonthlyEarnings(monthly);
          setWeeklyEarnings(weekly);
          setPendingTotal(pending);
          setPayouts(payoutsList);
        }
      } catch (error) {
        console.error('Error fetching payout records:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayoutRecords();
  }, [partner?.id, partner?.offers_flowers, partner?.offers_cleaning, canViewEarnings]);

  if (partnerAuthLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!canViewEarnings) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const getStatusBadge = (status: 'pending' | 'paid' | 'skipped') => {
    switch (status) {
      case 'paid':
        return (
          <Badge variant="outline" className="border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
            <CheckCircle className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="outline" className="border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">My Earnings</h1>
        <p className="text-muted-foreground">Earnings from services you've delivered — flowers, headstone cleanings, and more</p>
      </div>

      {/* Earnings Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">All Time Paid</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalEarnings.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Total paid out</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">${pendingTotal.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Awaiting payment</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${monthlyEarnings.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">{format(new Date(), 'MMMM yyyy')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${weeklyEarnings.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Current week</p>
          </CardContent>
        </Card>
      </div>

      {/* Payout History Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payout History</CardTitle>
          <CardDescription>Your completed services and payment status</CardDescription>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No payouts yet. Your earnings will appear here once you complete service deliveries.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Memorial</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedPayouts.map((payout) => (
                      <TableRow key={payout.id}>
                        <TableCell>{format(payout.deliveredAt, 'MMM d, yyyy')}</TableCell>
                        <TableCell>{payout.memorialName}</TableCell>
                        <TableCell className="capitalize">{payout.serviceType}</TableCell>
                        <TableCell className="text-right font-medium">
                          ${payout.amount.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-center">
                          {getStatusBadge(payout.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} to {Math.min(endIndex, payouts.length)} of {payouts.length} payouts
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monument Portal cross-link — only for dual-role partners */}
      {partner?.linked_monument_company_id && (
        <Card className="border-l-4 border-l-[#787559] bg-[#FFFEF5]">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6">
            <div>
              <h3 className="text-base font-semibold text-[#212121]">
                Looking for your profit share earnings?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Commission earnings from partner-fulfilled services are tracked in the Monument Portal.
              </p>
            </div>
            <Button
              onClick={() => navigate('/portal/earnings')}
              className="bg-[#787559] hover:bg-[#787559]/90 text-white shrink-0"
            >
              View Monument Earnings
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PartnerEarnings;
